/**
 * Компонент одного пользовательского сообщения Activity.
 * Рендерит контент, reply/scope preview и меню действий комментария,
 * а сами действия делегирует наружу через `onAction`.
 */
import { Fragment, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { ActionIcon, Menu, Text } from '@mantine/core';
import { IconDots } from '@tabler/icons-react';
import { EventCard } from '@ayocore/platform/client/components/Events';
import { UserAvatar } from '@ayocore/platform/identity/client/components/UserAvatar';
import { useUsersController } from '@ayocore/platform/identity/client/UsersController';
import type { ActivityChatItem } from '@ayocore/activity/common/types/activity-message';
import { formatFeedListDate } from '../utils/ActivityList.utils';
import { ActivityChatMessageContent } from './ActivityChatMessageContent';
import { ActivityChatMessageFiles } from './ActivityChatMessageFiles';
import { ActivityChatReplyHeader } from './ActivityChatReplyHeader';
import classes from './ActivityChatMessageItem.module.css';

type ActivityChatCommentItemProps = {
  item: ActivityChatItem;
  showAuthor?: boolean;
  onAction?: (item: ActivityChatItem, actionId: string) => void;
  onInsertUserMention?: (userId: number, label: string) => void;
};

const COMMENT_CARD_STYLE = {
  width: 'fit-content',
  maxWidth: '100%',
  minWidth: 240,
} as const;

function renderCommentActions(
  item: ActivityChatItem,
  opened: boolean,
  onOpenChange: (opened: boolean) => void,
  onAction?: (item: ActivityChatItem, actionId: string) => void
): ReactNode | undefined {
  if (!item.actions || item.actions.length === 0) {
    return undefined;
  }
  const firstCommandActionIndex = item.actions.findIndex(action =>
    action.id.startsWith('run_command:')
  );

  return (
    <Menu withArrow position='bottom-end' withinPortal opened={opened} onChange={onOpenChange}>
      <Menu.Target>
        <ActionIcon
          variant='subtle'
          size='xs'
          onClick={event => event.stopPropagation()}
          aria-label='Действия комментария'
        >
          <IconDots size={14} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {item.actions.map((action, index) => (
          <Fragment key={`${item.id}:${action.id}`}>
            {firstCommandActionIndex > 0 && index === firstCommandActionIndex ? (
              <Menu.Divider />
            ) : null}
            <Menu.Item
              color={action.color === 'red' ? 'red' : undefined}
              onClick={event => {
                event.stopPropagation();
                onAction?.(item, action.id);
              }}
            >
              {action.label}
            </Menu.Item>
          </Fragment>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

/**
 * Рендерит одно пользовательское сообщение в чате активности.
 *
 * UX-нюанс: меню действий (три точки) рендерится в правой части
 * messageRow на месте даты/времени и появляется только при ховере
 * на сообщение. Это убирает «дырку» от actions-слота справа от балуна.
 * Когда меню открыто (dropdown показан) — точки остаются видимыми
 * даже без ховера, чтобы dropdown не «терялся».
 */
export function ActivityChatCommentItem({
  item,
  showAuthor = false,
  onAction,
  onInsertUserMention,
}: ActivityChatCommentItemProps) {
  const [menuOpened, setMenuOpened] = useState(false);
  const [hovered, setHovered] = useState(false);
  const { getUser } = useUsersController();
  const cachedAuthor = item.author_id ? getUser(item.author_id) : undefined;
  const hasActions = Boolean(item.actions && item.actions.length > 0);
  const showActionsInline = hasActions && (hovered || menuOpened);
  const inlineMenu = showActionsInline
    ? renderCommentActions(item, menuOpened, setMenuOpened, onAction)
    : null;

  const handleContextMenu = (event: React.MouseEvent) => {
    if (!hasActions) return;
    event.preventDefault();
    setMenuOpened(true);
  };

  const card = (
    <EventCard
      key={item.id}
      background={item.is_own ? '#e5f8fa' : '#fff'}
      radius='lg'
      style={COMMENT_CARD_STYLE}
    >
      {!item.is_own && showAuthor ? (
        <Text size='xs' c='blue.6' fw={600} mb={2}>
          {item.author_name}
        </Text>
      ) : null}
      <ActivityChatReplyHeader item={item} />
      {item.files && item.files.length > 0 ? <ActivityChatMessageFiles files={item.files} /> : null}
      <div className={classes.messageRow}>
        <Text size='xs' className={classes.messageText}>
          <ActivityChatMessageContent item={item} onInsertUserMention={onInsertUserMention} />
        </Text>
        <div className={classes.messageTime}>
          <Text
            size='xs'
            c='dimmed'
            style={{ visibility: showActionsInline ? 'hidden' : 'visible' }}
          >
            {formatFeedListDate(item.created_at)}
            {item.edited ? ' • Отредактировано' : ''}
          </Text>
          {showActionsInline ? (
            <div className={classes.messageTimeOverlay}>{inlineMenu}</div>
          ) : null}
        </div>
      </div>
    </EventCard>
  );

  return (
    <div
      className={clsx(classes.commentRoot, item.is_own ? classes.commentRootOwn : null)}
      data-comment-uuid={item.comment_uuid}
      onContextMenu={handleContextMenu}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {item.is_own ? (
        card
      ) : (
        // Аватар чужого автора «висит» слева от балуна. Слот аватара
        // зарезервирован всегда (даже без showAuthor) — иначе балуны
        // в группе сообщений одного автора прыгают по горизонтали.
        <div className={classes.commentRow}>
          <div className={classes.avatarSlot}>
            {showAuthor ? (
              <UserAvatar
                avatarThumbUrl={cachedAuthor?.avatarThumbUrl}
                name={cachedAuthor?.name ?? item.author_name}
                size='sm'
              />
            ) : null}
          </div>
          <div className={classes.cardSlot}>{card}</div>
        </div>
      )}
    </div>
  );
}
